// src/features/imports/components/ProvenanceTimeline.jsx
//
// INGEST-REVIEW-SIDEBYSIDE — Per-field provenance disclosure panel.
//
// Collapsed by default. On first expand fires getFieldProvenance(diffId)
// and renders a step-by-step lineage trail.
//
// Props:
//   diffId            number — import_diffs.id; used to fetch provenance steps
//   currentProvenance string — short code or label for the current value's source
//   incomingSource    string — source identifier for the incoming value
//
// Renders:
//   - Two static lines showing current vs incoming provenance
//   - "source unknown" when provenance fields are absent
//   - Loading state while the API call is in flight
//   - Each history step as:  source · actor · date
//   - Steps with source === "manual" get a "manual, protected" badge
//   - Empty steps → "Origin not recorded" (never inferred values)
//   - API error → compact inline message (no toast)

import React, { useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getFieldProvenance } from "../../../data/importSessionsClient.js";

// ─── Source label map ─────────────────────────────────────────────────────────

const PROV_SOURCE_LABELS = {
  s:               "Spacebase",
  spacebase:       "Spacebase",
  spacebase_main_leases:      "Spacebase",
  spacebase_critical_dates:   "Spacebase",
  spacebase_recurring_leases: "Spacebase",
  spacebase_contacts:         "Spacebase",
  spacebase_documents:        "Spacebase",
  m:               "Manual",
  manual:          "Manual",
  manual_csv:      "Manual",
  p:               "PDF",
  pdf:             "PDF",
  pdf_abstraction: "PDF",
  c:               "Client",
  client:          "Client",
  computed:        "Computed",
};

function provLabel(raw) {
  if (!raw) return null;
  return PROV_SOURCE_LABELS[String(raw).toLowerCase()] || String(raw);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtStepDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return String(iso); }
}

// ─── Static per-side provenance line ──────────────────────────────────────────

function ProvLine({ label, source, actor, date }) {
  const sourceLabel = provLabel(source) || "source unknown";
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: T.fsXxs, color: T.mid, padding: "3px 0",
      }}
    >
      <span style={{ fontWeight: T.fwSemibold, color: T.dark, minWidth: 60 }}>
        {label}
      </span>
      <span style={{ color: T.light }}>·</span>
      <span style={{ color: sourceLabel === "source unknown" ? T.light : T.dark, fontStyle: sourceLabel === "source unknown" ? "italic" : "normal" }}>
        {sourceLabel}
      </span>
      {actor && (
        <>
          <span style={{ color: T.light }}>·</span>
          <span>{actor}</span>
        </>
      )}
      {date && (
        <>
          <span style={{ color: T.light }}>·</span>
          <span style={{ color: T.light }}>{fmtStepDate(date)}</span>
        </>
      )}
    </div>
  );
}

// ─── ProvenanceTimeline ───────────────────────────────────────────────────────

// Derive the most recent step matching a source code (or fall back to last step overall)
function deriveStepDetail(steps, sourceCode) {
  if (!steps || steps.length === 0) return null;
  if (sourceCode) {
    const key = String(sourceCode).toLowerCase();
    const match = [...steps].reverse().find(
      (s) => s.source && String(s.source).toLowerCase() === key
    );
    if (match) return match;
  }
  return steps[steps.length - 1];
}

export function ProvenanceTimeline({ diffId, currentProvenance, incomingSource }) {
  const [expanded,     setExpanded]     = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [steps,        setSteps]        = useState(null);  // null = not yet fetched
  const [error,        setError]        = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [rawText,      setRawText]      = useState(null);
  const [conflict,     setConflict]     = useState(null);
  const loadedRef  = useRef(false);

  const handleToggle = () => {
    if (!expanded && !loadedRef.current) {
      loadedRef.current = true;
      setLoading(true);
      getFieldProvenance(diffId)
        .then((data) => {
          setSteps(Array.isArray(data.steps) ? data.steps : []);
          setDocumentName(data.documentName ?? null);
          setRawText(data.rawText           ?? null);
          setConflict(data.conflict         ?? null);
          setLoading(false);
        })
        .catch((err) => {
          setError(err?.message || "Could not load provenance");
          setLoading(false);
        });
    }
    setExpanded((p) => !p);
  };

  // Derive actor/date for the Current side from the fetched history once loaded
  const currentDetail = steps !== null ? deriveStepDetail(steps, currentProvenance) : null;

  return (
    <div data-testid={`provenance-timeline-${diffId}`}>
      {/* ── Collapse/expand toggle ── */}
      <button
        type="button"
        data-testid={`provenance-toggle-${diffId}`}
        onClick={handleToggle}
        style={{
          display: "flex", alignItems: "center", gap: 5, width: "100%",
          padding: "5px 10px",
          background: T.bg, border: "none", borderTop: `1px solid ${T.border}`,
          fontSize: T.fsXxs, color: T.mid, cursor: "pointer",
          textAlign: "left", fontWeight: T.fwSemibold,
        }}
      >
        <span style={{ fontSize: "9px", color: T.light }}>
          {expanded ? "▾" : "▸"}
        </span>
        Provenance
      </button>

      {/* ── Panel ── */}
      {expanded && (
        <div
          data-testid={`provenance-panel-${diffId}`}
          style={{
            padding: "8px 10px",
            borderTop: `1px solid ${T.border}`,
            background: T.bg,
          }}
        >
          {/* ── Per-side static provenance summary ── */}
          <div
            data-testid="provenance-sides"
            style={{
              marginBottom: 6,
              paddingBottom: 6,
              borderBottom: steps && steps.length > 0 ? `1px solid ${T.border}` : "none",
            }}
          >
            <ProvLine
              label="Current"
              source={currentProvenance}
              actor={currentDetail?.actor}
              date={currentDetail?.occurredAt}
            />
            <ProvLine label="Incoming" source={incomingSource} />
            {documentName && (
              <div
                data-testid={`provenance-source-doc-${diffId}`}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  fontSize: T.fsXxs, color: T.mid, padding: "3px 0",
                }}
              >
                <span style={{ fontWeight: T.fwSemibold, color: T.dark, minWidth: 60 }}>
                  Source
                </span>
                <span style={{ color: T.light }}>·</span>
                <span>📄 Extracted from: <strong style={{ color: T.dark }}>{documentName}</strong></span>
              </div>
            )}
            {rawText && (
              <div
                data-testid={`provenance-raw-text-${diffId}`}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 6,
                  fontSize: T.fsXxs, color: T.mid, padding: "3px 0",
                }}
              >
                <span style={{ fontWeight: T.fwSemibold, color: T.dark, minWidth: 60, flexShrink: 0 }}>
                  From text
                </span>
                <span style={{ color: T.light, flexShrink: 0 }}>·</span>
                <span style={{ fontStyle: "italic" }}>
                  &ldquo;{rawText.length > 150 ? rawText.slice(0, 150) + "\u2026" : rawText}&rdquo;
                </span>
              </div>
            )}
            {conflict?.document && (
              <div
                data-testid={`provenance-conflict-${diffId}`}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 6,
                  fontSize: T.fsXxs, color: T.mid, padding: "3px 0",
                }}
              >
                <span style={{ fontWeight: T.fwSemibold, color: T.amberDark, minWidth: 60, flexShrink: 0 }}>
                  ⚠ Conflict
                </span>
                <span style={{ color: T.light, flexShrink: 0 }}>·</span>
                <span>
                  {conflict.document}:{" "}
                  <strong style={{ color: T.dark }}>{String(conflict.value ?? "")}</strong>
                </span>
              </div>
            )}
          </div>

          {loading && (
            <div
              data-testid="provenance-loading"
              style={{ fontSize: T.fsXxs, color: T.mid, fontStyle: "italic" }}
            >
              Loading…
            </div>
          )}

          {error && !loading && (
            <div
              data-testid="provenance-error"
              style={{
                fontSize: T.fsXxs, color: T.errorText,
                padding: "3px 0",
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && steps !== null && steps.length === 0 && (
            <div
              data-testid="provenance-empty"
              style={{ fontSize: T.fsXxs, color: T.light, fontStyle: "italic" }}
            >
              Origin not recorded
            </div>
          )}

          {!loading && !error && steps !== null && steps.length > 0 && (
            <ol
              data-testid="provenance-steps"
              style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 0 }}
            >
              {steps.map((step, idx) => {
                const isManual = step.source === "manual";
                return (
                  <li
                    key={idx}
                    data-testid={`provenance-step-${idx}`}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 8,
                      padding: "5px 0",
                      borderBottom: idx < steps.length - 1 ? `1px solid ${T.border}` : "none",
                      fontSize: T.fsXxs,
                    }}
                  >
                    {/* Step dot */}
                    <span
                      style={{
                        width: 7, height: 7, borderRadius: "50%", marginTop: 3, flexShrink: 0,
                        background: isManual ? T.provenanceTooltipManual : T.light,
                      }}
                    />
                    {/* Source */}
                    <span style={{ fontWeight: T.fwSemibold, color: T.dark, flexShrink: 0 }}>
                      {step.source ?? "—"}
                    </span>
                    <span style={{ color: T.light, flexShrink: 0 }}>·</span>
                    {/* Actor */}
                    <span style={{ color: T.mid, flexShrink: 0 }}>
                      {step.actor ?? "—"}
                    </span>
                    {/* Manual-protected badge */}
                    {isManual && (
                      <span
                        data-testid={`manual-badge-${idx}`}
                        style={{
                          fontSize: T.fsXxxs, fontWeight: T.fwBold,
                          padding: "1px 5px", borderRadius: 4,
                          background: T.ragRedBg, color: T.ragRed,
                          flexShrink: 0, whiteSpace: "nowrap",
                        }}
                      >
                        manual, protected
                      </span>
                    )}
                    {/* Date */}
                    <span style={{ color: T.light, marginLeft: "auto", whiteSpace: "nowrap" }}>
                      {fmtStepDate(step.occurredAt)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
